import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {ArticleEngagementSidePanelContent} from '../../../../../../libs/knowledge-base/shared/components/insights/articleEngagementSidePanelContent';
import {KnowledgeBasePluginArticlePreview} from '../../../../../../libs/knowledge-base/shared/components/plugin/knowledgeBasePluginArticlePreview';
import {KnowledgeBaseAnalyticsReportQueryParams} from '../../../../../../libs/knowledge-base/shared/helpers/analyticsQueryHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {KnowledgeBaseSupportedLanguagesTypesEnum} from '../../../../../../libs/shared/core/src/models/api/knowledgeBaseModel';
import {useAppQuery} from '../../../../../../libs/shared/core/src/services/query/useAppQuery';
import {getKnowledgeBases} from '../../../../../../libs/shared/core/src/store/data/knowledgeBases/knowledgeBasesSelectors';
import {Skeleton} from '../../../../../../libs/shared/ui/src/components/utility/skeleton';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {AppState} from '../../../../../store/appState';
import {buildAnalyticsAutomationQueryFromState} from '../../../../../store/panels/analytics/analyticsPanelHelpers';
import {knowledgeBaseReportArticleQueries} from '../queries/knowledgeBaseReportArticleQueries';
import {KnowledgeBaseReportSidePanelErrorState} from './knowledgeBaseReportSidePanelErrorState';

/*
 * Constants.
 */

export const enum KnowledgeBaseReportSidePanelTabsEnum {
  PREVIEW = 'preview',
  INSIGHTS = 'insights',
}

/*
 * Props.
 */

interface KnowledgeBaseReportSidePanelArticleProps {
  knowledgeBaseId: number;
  articleId: number;
  shouldDisplayDraft?: boolean;
  locale?: KnowledgeBaseSupportedLanguagesTypesEnum;
  selectedTab: KnowledgeBaseReportSidePanelTabsEnum;
}

/*
 * Style.
 */

const StyledContentWrapperDiv = styled.div`
  padding: 16px;
  display: flex;
  flex-flow: column;
  gap: 8px;
  overflow-x: hidden;
  overflow-y: auto;
`;

const StyledLoadingParagraphDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 6px;
`;

const StyledInsightsContentWrapperDiv = styled.div`
  overflow: auto;
`;

/*
 * Component.
 */

export const KnowledgeBaseReportSidePanelArticle: FC<KnowledgeBaseReportSidePanelArticleProps> = ({
  articleId,
  knowledgeBaseId,
  locale,
  shouldDisplayDraft,
  selectedTab,
}) => {
  const knowledgeBase = useAppSelector(getKnowledgeBases)[knowledgeBaseId];
  const {data, asyncStatus} = useAppQuery(knowledgeBaseReportArticleQueries.byId(articleId, locale));
  const queryParams: KnowledgeBaseAnalyticsReportQueryParams = useAppSelector((state: AppState) =>
    buildAnalyticsAutomationQueryFromState(state),
  );
  const localeRevision = useMemo(() => {
    if (!knowledgeBase || !data) {
      return undefined;
    }
    return data?.translations[locale || knowledgeBase.defaultLocale];
  }, [knowledgeBase, data, locale]);

  // Loading state while we check for the validity of an article
  if (asyncStatus === AsyncStatusesEnum.LOADING) {
    return <>{renderLoadingState()}</>;
  }
  // If we failed to find a locale revision or the fetch failed.
  if (asyncStatus === AsyncStatusesEnum.FAILED || !localeRevision) {
    return <KnowledgeBaseReportSidePanelErrorState />;
  }

  return (
    <>
      {selectedTab === KnowledgeBaseReportSidePanelTabsEnum.PREVIEW ? (
        <StyledContentWrapperDiv>
          <KnowledgeBasePluginArticlePreview
            knowledgeBaseId={knowledgeBaseId}
            locale={locale}
            articleId={articleId}
            shouldDisplayDraft={shouldDisplayDraft}
          />
        </StyledContentWrapperDiv>
      ) : (
        <StyledInsightsContentWrapperDiv>
          <ArticleEngagementSidePanelContent articleId={articleId} queryParams={queryParams} />
        </StyledInsightsContentWrapperDiv>
      )}
    </>
  );
};

/*
 * Helpers.
 */

function renderLoadingState() {
  return (
    <StyledContentWrapperDiv>
      <Skeleton height="28px" />
      <Skeleton height="12px" width="50%" />
      <Skeleton height="18px" />
      <StyledLoadingParagraphDiv>
        <Skeleton height="14px" />
        <Skeleton height="14px" />
        <Skeleton height="14px" />
      </StyledLoadingParagraphDiv>
      <br />
      <StyledLoadingParagraphDiv>
        <Skeleton height="14px" />
        <Skeleton height="14px" />
        <Skeleton height="14px" />
      </StyledLoadingParagraphDiv>
      <br />
      <StyledLoadingParagraphDiv>
        <Skeleton height="14px" />
        <Skeleton height="14px" />
        <Skeleton height="14px" />
      </StyledLoadingParagraphDiv>
    </StyledContentWrapperDiv>
  );
}
